import React, { useState } from 'react';
import { FormControl, Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material';
import { getInputValue } from '../../../../../common/react';
import { DefaultInput } from '../../../../../common/Styleguide/Common/Input';
import { COLORS } from '../../../../../common/Styleguide/Common/colors';
import { Box } from '@mui/system';
import { StyledLoadingButton } from '../../../../../common/Styleguide/Common/Button';
import { DeveloperProfile, granularSaveAccount } from '../profile-graphql';
import { toast } from 'react-toastify';

export interface Props {
  profile: DeveloperProfile;
}

const MAX_CONTACT_NAME_LENGTH = 50;

const Account: React.FC<Props> = ({ profile }: Props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { spacing } = useTheme();

  const [contactName, setContactName] = useState<string>(profile.name);
  const [contactNameError, setContactNameError] = useState<string | null>(null);
  const [receiveUpdates, setReceiveUpdates] = useState<boolean>(!profile.hasOptedOutProductUpdateEmails);

  const onSaveClick = async () => {
    setIsSaving(true);
    try {
      await granularSaveAccount({
        name: contactName,
        hasOptedOutProductUpdateEmails: !receiveUpdates,
      });
    } catch (e) {
      console.error('Failed to save account data', JSON.stringify(e, null, 2));
      toast.error('Failed to save account data', { autoClose: false });
    } finally {
      setIsSaving(false);
    }
  };

  const updateContactName = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const name = getInputValue(ev);
    const isValid = name.length < MAX_CONTACT_NAME_LENGTH && name.trim().length > 0;
    setContactName(name);
    setContactNameError(isValid ? null : 'Invalid contact name');
  };

  const canSave = () => {
    return !contactNameError;
  };

  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Account
      </Typography>

      <div style={{ fontSize: 16, marginBottom: spacing(2), color: COLORS.white[60] }}>
        The contact name is used in the emails that we send to the provided email address.
      </div>

      <div style={{ width: '50%' }}>
        <DefaultInput
          sx={{ mb: 2 }}
          value={contactName}
          onChange={updateContactName}
          header="Contact name"
          error={!!contactNameError}
          errorText={`${contactNameError}`}
        />
        <DefaultInput readOnly value={profile.email} header="Email" />
      </div>

      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2, mb: 2 }}>
        <FormControl>
          <FormControlLabel
            control={<Checkbox checked={receiveUpdates} onChange={(ev) => setReceiveUpdates(ev.target.checked)} />}
            label="I want to receive product updates from CrazyGames on my account e-mail"
          />
        </FormControl>
      </Box>

      <StyledLoadingButton loading={isSaving} onClick={onSaveClick} disabled={!canSave()}>
        Save changes
      </StyledLoadingButton>
    </>
  );
};

export default Account;
